import React, { useEffect, useMemo, useState } from 'react';
import { MapPin } from 'lucide-react';
import useTokenValidation from 'hooks/useTokenValidation';
import { CompanyChangeBlur } from 'components/common';
import PageLayout from 'components/common/PageLayout';
import SearchableFilterSelect from 'components/common/SearchableFilterSelect';
import { setDefaultCompanyContext, hasCompanyContext } from 'utils/setCompanyContext';
import useFaturamentoContratos from 'features/contratos/hooks/useFaturamentoContratos';
import FaturamentoTipoPessoaDashboard from 'features/contratos/components/FaturamentoTipoPessoaDashboard';
import { DateRangeFilterBlur } from 'components/filters';

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const today = new Date();
  const start = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1));
  const end = formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
  return {
    presetId: 'mtd',
    start,
    end,
    data_inicio: start,
    data_fim: end
  };
};

const DashboardContratos = () => {
  useTokenValidation();

  useEffect(() => {
    if (!hasCompanyContext()) {
      setDefaultCompanyContext();
    }
  }, []);

  const [range, setRange] = useState(() => getDefaultDateRange());
  const [cidadeSelecionada, setCidadeSelecionada] = useState('todas');
  const {
    resumo,
    loading,
    initialLoading,
    error,
    ultimaAtualizacao,
    recarregar
  } = useFaturamentoContratos(range);

  const periodoTexto = useMemo(() => {
    const dataInicio = range?.start || range?.data_inicio;
    const dataFim = range?.end || range?.data_fim;

    if (!dataInicio || !dataFim) {
      return 'Período não selecionado';
    }

    const formatar = (value) => {
      const [ano, mes, dia] = value.split('-');
      return `${dia}/${mes}/${ano}`;
    };

    if (dataInicio === dataFim) {
      return formatar(dataInicio);
    }

    return `${formatar(dataInicio)} a ${formatar(dataFim)}`;
  }, [range]);

  const opcoesCidade = useMemo(() => {
    const cidades = Array.isArray(resumo?.cidades) ? resumo.cidades : [];
    const cidadesUnicas = new Map();

    cidades.forEach((cidade) => {
      const nomeCidade = String(cidade?.cidadeNome ?? cidade?.cidade_nome ?? cidade?.nome ?? '')
        .trim();

      if (!nomeCidade) {
        return;
      }

      const chave = nomeCidade.toLowerCase();
      if (!cidadesUnicas.has(chave)) {
        cidadesUnicas.set(chave, nomeCidade);
      }
    });

    return Array.from(cidadesUnicas.values())
      .sort((left, right) => left.localeCompare(right, 'pt-BR'));
  }, [resumo?.cidades]);

  useEffect(() => {
    if (cidadeSelecionada === 'todas') {
      return;
    }

    if (!opcoesCidade.includes(cidadeSelecionada)) {
      setCidadeSelecionada('todas');
    }
  }, [cidadeSelecionada, opcoesCidade]);

  const handleRecarregar = () => {
    recarregar(range);
  };

  const isLoadingOverlayVisible = initialLoading || loading;
  const loadingTitle = initialLoading ? 'Carregando' : 'Atualizando';
  const loadingMessage = initialLoading
    ? 'Buscando faturamento, planos e composição da carteira'
    : 'Sincronizando os dados da empresa selecionada';

  if (error && !loading && !initialLoading) {
    return (
      <PageLayout
        showRefreshButton={true}
        customRefreshFunction={handleRecarregar}
      >
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-6 shadow-[0_16px_40px_rgba(2,6,23,0.18)]">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-300">
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
                  Faturamento indisponível
                </h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[color:var(--text-muted)]">
                  Não foi possível carregar o faturamento de contratos neste momento. Tente novamente ou confira a integração com o n8n.
                </p>
                <button
                  onClick={handleRecarregar}
                  className="mt-4 rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface)]"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <>
      <CompanyChangeBlur />
      <PageLayout
        showRefreshButton={true}
        customRefreshFunction={handleRecarregar}
      >
        <div className="relative max-w-full mx-auto overflow-x-hidden px-4 py-6 sm:px-6 lg:px-8">
          {isLoadingOverlayVisible && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-sm bg-black/20">
              <div
                className="rounded-3xl border border-[color:var(--border)] bg-[color:var(--surface)] px-6 py-5"
                style={{ boxShadow: 'var(--shadow)' }}
              >
                <div className="flex items-center gap-4">
                  <div className="loader-elegant" aria-hidden="true"></div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--text-muted)]">
                      {loadingTitle}
                    </p>
                    <p className="mt-1 text-sm text-[color:var(--text-primary)]">
                      {loadingMessage}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">Dashboard de Contratos</h2>
                {loading ? (
                  <div className="flex items-center text-sm text-[color:var(--accent)]">
                    <svg className="h-4 w-4 mr-1 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    Atualizando...
                  </div>
                ) : null}
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm text-[color:var(--text-muted)]">
                <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>
                  <span className="font-medium text-[color:var(--text-primary)]">Período:</span> {periodoTexto}
                </span>
              </div>
            </div>

            <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto lg:items-center lg:justify-end">
              <div className="w-full sm:min-w-[250px] lg:w-[280px]">
                <SearchableFilterSelect
                  options={opcoesCidade}
                  value={cidadeSelecionada}
                  onChange={(value) => setCidadeSelecionada(value || 'todas')}
                  allOptionLabel="Todas as cidades"
                  allOptionValue="todas"
                  placeholder="Buscar cidade..."
                  ariaLabel="Filtrar por cidade"
                  icon={MapPin}
                />
              </div>

              <DateRangeFilterBlur
                value={range}
                onChange={(novoRange) => setRange({
                  presetId: novoRange?.presetId || 'custom',
                  start: novoRange?.start || novoRange?.data_inicio || '',
                  end: novoRange?.end || novoRange?.data_fim || '',
                  data_inicio: novoRange?.start || novoRange?.data_inicio || '',
                  data_fim: novoRange?.end || novoRange?.data_fim || ''
                })}
              />
            </div>
          </div>

          <FaturamentoTipoPessoaDashboard
            resumo={resumo}
            loading={loading}
            ultimaAtualizacao={ultimaAtualizacao}
            cidadeSelecionada={cidadeSelecionada}
          />
        </div>
      </PageLayout>
    </>
  );
};

export default DashboardContratos;
